import { Attachemnt } from "./../../model/attachment.model";
import {
  Component,
  OnInit,
  HostListener,
  Output,
  EventEmitter,
  Input,
  ViewChild,
} from "@angular/core";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NgForm } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { SendingIdService } from "src/app/services/sending-id.service";
import { RequirementService } from "src/app/services/requirement.service";
import { ChunkedUploadService } from "src/app/services/chunked-upload.service";
import { ConfirmActionModalComponent } from "@app/trackvision/shared/components/confirm-action-modal/confirm-action-modal.component";
import { AttachmentViewComponent } from "../../components/attachment-view/attachment-view.component";
import { isAttachmentCategoryComplete } from "../../../util/attachment-categories";

/** trackvision-attachment: completa en S3 permanente + row attachment.
 *  temp-then-parent: chunked a temporaryfiles; el padre asocia (cliente legado, etc.). */
export type AttachmentUploadMode = 'trackvision-attachment' | 'temp-then-parent';

@Component({
    selector: "app-attachment",
    templateUrl: "./attachment.component.html",
    styleUrls: ["./attachment.component.scss"],
    standalone: false
})
export class AttachmentComponent implements OnInit {
  @Input() service: string;
  @Input() typeId: string;
  /** Override del id (si no se usa SendingIdService). */
  @Input() entityId: number;
  @Input() uploadMode: AttachmentUploadMode = 'trackvision-attachment';
  /**
   * Si false, al terminar temp-then-parent no se cierra solo:
   * el padre asocia y cierra (detalle Contraparte / sidebar docs).
   */
  @Input() autoDismissAfterTempUpload = true;
  /** Categoría obligatoria por archivo (sidebar Archivos de requerimiento). */
  @Input() enableCategory = false;
  @Output() insertAttach = new EventEmitter<boolean>();
  /** IDs de common.temporaryfiles tras carga chunked (modo temp-then-parent). */
  @Output() uploadedTempIds = new EventEmitter<Array<{ id: number }>>();
  @ViewChild('attachmentView') attachmentView: AttachmentViewComponent;
  files: any[] = [];
  loading: boolean = false;
  uploadProgress = 0;
  uploadLabel = '';
  private uploadCancelled = false;
  sentId: number;
  newAttachment: Attachemnt = new Attachemnt();
  errorMessage: string = "";
  successMessage: string = "";
  private _success = new Subject<string>();
  private _error = new Subject<string>();

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private sendIdServ: SendingIdService,
    private _requirements: RequirementService,
    private chunkedUpload: ChunkedUploadService
  ) {}

  ngOnInit() {
    this.sendIdServ.currentMessage.subscribe((id) => {
      if (this.entityId == null) {
        this.sentId = id;
      }
    });
    if (this.entityId != null) {
      this.sentId = this.entityId;
    }
    this.messageSubmitForm();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent) {
    if (this.loading) {
      $event.returnValue = true;
    }
  }

  async closeModal() {
    if (this.loading) {
      const confirmModal = this.modalService.open(ConfirmActionModalComponent, {
        size: 'sm',
        centered: true,
      });
      confirmModal.componentInstance.titel = 'Carga en curso';
      confirmModal.componentInstance.message =
        'La carga no ha terminado. ¿Desea salir de todos modos?';
      const confirmed = await confirmModal.result.catch(() => false);
      if (!confirmed) {
        return;
      }
      // Aceptar → cancelación explícita: shouldCancel + abort inmediato de sesiones activas
      this.uploadCancelled = true;
      try {
        await this.chunkedUpload.abortActiveUploads();
      } catch {
        // ignore
      }
      this.loading = false;
    }
    this.activeModal.close("Modal Closed");
  }

  dismissWithConfirm() {
    void this.closeModal();
  }

  getFormData(id: string, body) {
    const formData: FormData = new FormData();
    formData.append("file", body.file);
    formData.append(id, String(body.requirementid));
    formData.append("note", body.note);
    if (body.category) {
      formData.append("category", body.category);
    }
    if (body.categoryother) {
      formData.append("categoryother", body.categoryother);
    }
    return formData;
  }

  private validateCategories(): boolean {
    if (!this.enableCategory) {
      return true;
    }
    if (this.attachmentView && !this.attachmentView.areCategoriesValid()) {
      this._error.next("Error: Debe seleccionar categoría en todos los archivos.");
      return false;
    }
    if (this.files.some((f) => !isAttachmentCategoryComplete(f))) {
      this._error.next("Error: Si elige Otro, indique el nombre de la categoría.");
      return false;
    }
    return true;
  }

  async submitForm(form: NgForm) {
    if (!this.files.length) {
      this._error.next("Error: No sé adjuntado ningún archivo!!!");
      return;
    }

    if (!this.validateCategories()) {
      return;
    }

    if (this.uploadMode === 'temp-then-parent') {
      await this.submitChunkedTempForParent();
      return;
    }

    if (!this.sentId) {
      this._error.next("Error: No se encontró el identificador del registro.");
      return;
    }

    // Solo trackvision usa carga fragmentada; globalvision sigue el upload clásico
    if (this.service === 'trackvision') {
      await this.submitChunkedTrackvision();
      return;
    }

    this.submitLegacy();
  }

  private async submitChunkedTempForParent() {
    this.loading = true;
    this.uploadCancelled = false;
    this.uploadProgress = 0;

    try {
      const toUpload = this.files
        .filter((f) => f && typeof f.size === 'number' && f.size > 0 && f.name)
        .map((f) => {
          const wrapped = f as File & { note?: string; category?: string; categoryother?: string };
          wrapped.note = f.note || '';
          wrapped.category = f.category || '';
          wrapped.categoryother = f.categoryother || '';
          return wrapped;
        });

      const ids = await this.chunkedUpload.uploadFilesAsTemp(
        toUpload,
        (percent, fileName) => {
          this.uploadProgress = percent;
          this.uploadLabel = fileName;
        },
        () => this.uploadCancelled
      );

      if (ids.length !== toUpload.length) {
        throw new Error(`No se subieron todos los archivos (${ids.length}/${toUpload.length})`);
      }

      this.uploadProgress = 100;
      this.uploadLabel = this.autoDismissAfterTempUpload
        ? 'Completado'
        : 'Vinculando documentos…';
      this.uploadedTempIds.emit(ids);
      this.insertAttach.emit(true);
      this._success.next("Se realizo correctamente lo solicitado !!!");

      if (this.autoDismissAfterTempUpload) {
        this.loading = false;
        this.activeModal.dismiss();
      }
      // Si autoDismiss=false, el padre asocia, refresca y cierra el modal.
    } catch (err) {
      this.loading = false;
      if (this.uploadCancelled || err?.message === 'Carga cancelada') {
        return;
      }
      const msg = err?.error?.error?.message || err?.message || "Error: No se pudo guardar los archivos.";
      this._error.next(msg);
    }
  }

  private async submitChunkedTrackvision() {
    this.loading = true;
    this.uploadCancelled = false;
    this.uploadProgress = 0;

    try {
      for (const file of this.files) {
        this.uploadLabel = file.name;
        await this.chunkedUpload.uploadFile(file, {
          purpose: 'attachment',
          note: file.note || '',
          category: file.category || '',
          categoryother: file.categoryother || '',
          requirementId: this.sentId,
          shouldCancel: () => this.uploadCancelled,
          onProgress: (percent, fileName) => {
            this.uploadProgress = percent;
            this.uploadLabel = fileName;
          }
        });
      }
      this.loading = false;
      this._success.next("Se realizo correctamente lo solicitado !!!");
      this.activeModal.dismiss();
      this.insertAttach.emit(true);
    } catch (err) {
      this.loading = false;
      if (this.uploadCancelled || err?.message === 'Carga cancelada') {
        return;
      }
      const msg = err?.error?.error?.message || err?.message || "Error: No se pudo guardar los archivos.";
      this._error.next(msg);
    }
  }

  private submitLegacy() {
    this.loading = true;
    this.files.forEach((file) => {
      const body: {
        requirementid: number;
        note: string;
        category?: string;
        categoryother?: string;
        file: File;
      } = {
        requirementid: this.sentId,
        note: file.note,
        category: file.category,
        categoryother: file.categoryother,
        file,
      };
      const formData = this.getFormData(this.typeId, body);

      this._requirements.uploadOneFile(formData, this.service).subscribe((resp) => {
        if (resp["error"]) {
          if (resp["error"].message) {
            this.loading = false;
            this._error.next(resp["error"].message);
          } else {
            this.loading = false;
            this._error.next("Error: No se pudo guardar los archivos.");
          }
        } else {
          this.loading = false;
          this._success.next("Se realizo correctamente lo solicitado !!!");
          this.activeModal.dismiss();
          this.insertAttach.emit(true);
        }
      });
    });
  }

  retrieveFiles(files) {
    this.files = files;
  }

  messageSubmitForm() {
    this._success.subscribe((message) => (this.successMessage = message));
    this._error.subscribe((message) => (this.errorMessage = message));
    this._success.pipe(debounceTime(5000)).subscribe(() => (this.successMessage = ""));
    this._error.pipe(debounceTime(5000)).subscribe(() => (this.errorMessage = ""));
  }
}
